import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useMetrics } from 'appSelectorContext';

import { METRIC_PUBLISHER_NAMES } from '../../constants';
import { Context, Types } from '../../store';
import {
  MetricPair,
  MetricSeverity,
  MetricType,
  ValidationStatus,
  ValidationStatusType,
  URIParams,
  ValidateSerialMetadataResponse,
} from '../../store/types';
import { useService } from '../../store/useService';
import { VALIDATE_SERIAL, VALIDATE_SERIALS } from './ScanSerialService';
import { SERIAL_ID_VALIDATION_FAILED_METRIC, WARNING_ERROR_CODES } from './constants';

const DEFAULT_ERROR_STATUS: ValidationStatus = {
  validationStatusType: ValidationStatusType.ERROR,
  statusCode: 'network_error',
  statusMessage: '',
};

export const useScanSerial = (containerId?: string) => {
  const { dispatch }: any = useContext(Context);
  let { modelId }: any = useParams();
  const [error, setError] = useState<undefined | string>();
  const context = useMetrics();
  const { client } = useService();

  // Instance will provide ApolloGraphQl instance or HTTP Fetch instance based on application type
  const clientInstance = client();

  const publisher = context.initialMetricsPublisher.newChildActionPublisherForMethod(
    METRIC_PUBLISHER_NAMES.SCAN_SERIAL,
  );
  const urlParam = useParams<URIParams>();
  const isRma = urlParam.receiveType === 'no_reference';

  useEffect(() => {
    setError(undefined);
  }, [containerId]);

  /* istanbul ignore next */
  const validateSerial = async ({ variables, onCompleted, onError }: any) => {
    try {
      const { data } = await clientInstance.query({
        query: VALIDATE_SERIAL,
        variables,
      });
      const serialId = data?.validateSerialMetadata?.serialId;

      const status: ValidationStatus = data?.validateSerialMetadata?.status;
      let metric: MetricPair | null = null;

      const externalReferenceValue: string = data?.validateSerialMetadata?.externalReferenceValue;
      const validationStatus = externalReferenceValue
        ? {
            validationStatusType: ValidationStatusType.ERROR,
            statusCode: 'SERIAL_EXPECTED_IN_SHIPMENT',
          }
        : status;

      /* istanbul ignore next */
      if (
        status.statusCode &&
        status.validationStatusType === ValidationStatusType.WARNING &&
        WARNING_ERROR_CODES.includes(status.statusCode)
      ) {
        metric = {
          severity: MetricSeverity.WARNING,
          metricType: MetricType.MISMATCH_AGAINST_ASN,
        };
      }

      onCompleted &&
        onCompleted({
          payload: {
            containerId: containerId,
            serialNumber: serialId,
            modelId,
            status: validationStatus,
            loading: false,
            called: true,
            externalReferenceValue: externalReferenceValue,
            referenceLineItemId: variables?.referenceLineItemId,
            metric,
          },
        });
    } catch (e) {
      setError('network_error');
      publisher.publishCounterMonitor(SERIAL_ID_VALIDATION_FAILED_METRIC, 1);
      dispatch({
        type: Types.scanSerialComplete,
        payload: {
          containerId: containerId,
          serialNumber: variables?.serialId,
          id: variables?.id,
          modelId,
          status: DEFAULT_ERROR_STATUS,
          loading: false,
          externalReferenceValue: '',
          referenceLineItemId: variables?.referenceLineItemId,
        },
      });
    }
  };

  const validateSerials = async ({ variables, onCompleted, onError }: any) => {
    try {
      const { data } = await clientInstance.query({
        query: VALIDATE_SERIALS,
        variables,
      });
      const payload: ValidateSerialMetadataResponse[] = data?.validateSerialsMetadata?.serials;
      onCompleted &&
        onCompleted({
          serials: payload.map((p: ValidateSerialMetadataResponse) => ({
            containerId: containerId,
            serialNumber: p.serialId,
            modelId,
            status: p.status,
            loading: false,
            externalReferenceValue: isRma ? null : p.externalReferenceValue,
            called: true,
            id: p.id,
            referenceLineItemId: variables?.[0]?.referenceLineItemId,
          })),
        });
    } catch (e) {
      setError('network_error');
      publisher.publishCounterMonitor(SERIAL_ID_VALIDATION_FAILED_METRIC, 1);
      onError && onError();
      variables?.serials?.forEach((p: ValidateSerialMetadataResponse) => {
        dispatch({
          type: Types.scanSerialComplete,
          payload: {
            containerId: containerId,
            serialNumber: p?.serialId,
            id: p?.id,
            modelId: p?.mpn,
            status: DEFAULT_ERROR_STATUS,
            loading: false,
            referenceLineItemId: p?.referenceLineItemId,
          },
        });
      });
    }
  };

  return {
    validateSerial,
    validateSerials,
    error,
  };
};

export default useScanSerial;
